"use client";

import { Link, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RootState } from "@/redux/store";
import { signOut } from "@aws-amplify/auth";
import { useSelector } from "react-redux";
import { trackEvent } from "@/utils/analytics";
import "./Header.css";
import HeaderMenuItem from "./HeaderMenuItem";

export default function Header() {
  const { currentUserEmail, userGroups } = useSelector(
    (state: RootState) => state.auth,
  );
  const location = useLocation();

  // TODO: Move this to a config file
  const menuItems = [
    {
      name: "Fax Review",
      groups: [],
      items: [
        {
          name: "Faxes to Review",
          href: "document-review",
        },
        {
          name: "Reviewed Faxes",
          href: "reviewed-faxes",
        },
      ],
    },
    {
      name: "Audit",
      groups: ["audit"],
      items: [
        {
          name: "Audit",
          href: "audit",
        },
      ],
    },
    {
      name: "Operations",
      groups: ["admin"],
      items: [
        {
          name: "Operations Dashboard",
          href: "ops-dashboard",
        },
      ],
    },
  ];

  const getMenuItems = () => {
    const availableItems = menuItems.filter(
      (menuItem) =>
        menuItem.groups.length === 0 ||
        menuItem.groups.some((group) => userGroups?.includes(group)),
    );

    return availableItems.map((menuItem) => (
      <HeaderMenuItem
        key={menuItem.name}
        name={menuItem.name}
        items={menuItem.items}
        isActive={menuItem.items.some(
          (item) => `/${item.href}` === location.pathname,
        )}
      />
    ));
  };

  return (
    <header className="w-full bg-white border-b">
      <div className="max-w-full mx-auto px-2 sm:px-6 lg:px-4">
        <div className="flex items-center justify-between h-full">
          <div className="flex items-center h-full">
            <svg
              data-testid="logo-svg"
              width="50"
              height="50"
              viewBox="0 0 1101 431"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M117.664 430.859C96.7656 430.859 77.332 425.391 59.3633 414.453C41.5898 403.516 27.2344 389.062 16.2969 371.094C5.55469 353.125 0.183594 333.301 0.183594 311.621V274.414C0.183594 257.617 3.40625 241.895 9.85156 227.246C16.2969 212.598 24.8906 199.707 35.6328 188.574C26.8438 179.004 19.9102 167.773 14.832 154.883C9.94922 141.797 7.50781 127.832 7.50781 112.988C7.50781 92.4805 12.3906 73.5352 22.1562 56.1523C31.9219 39.1602 45.2031 25.6836 62 15.7227C78.7969 5.76172 97.3516 0.78125 117.664 0.78125C138.172 0.78125 156.727 5.85938 173.328 16.0156C189.93 26.1719 203.211 39.7461 213.172 56.7383C223.133 73.5352 228.113 92.2852 228.113 112.988C228.113 127.832 225.574 141.699 220.496 154.59C215.613 167.285 208.777 178.613 199.988 188.574C210.73 199.316 219.227 212.109 225.477 226.953C231.727 241.797 234.852 257.617 234.852 274.414V311.621C234.852 333.301 229.578 353.223 219.031 371.387C208.289 389.551 194.129 404.004 176.551 414.746C158.973 425.488 139.344 430.859 117.664 430.859ZM117.664 153.125C128.211 153.125 137.098 149.219 144.324 141.406C151.746 133.594 155.457 124.121 155.457 112.988C155.457 101.855 151.746 92.2852 144.324 84.2773C137.098 76.2695 128.211 72.2656 117.664 72.2656C107.312 72.2656 98.3281 76.2695 90.7109 84.2773C83.0938 92.2852 79.2852 101.855 79.2852 112.988C79.2852 123.73 83.0938 133.105 90.7109 141.113C98.3281 149.121 107.312 153.125 117.664 153.125ZM117.664 359.082C130.164 359.082 140.711 354.492 149.305 345.312C157.898 336.133 162.195 324.902 162.195 311.621V274.414C162.195 261.523 157.898 250.391 149.305 241.016C140.711 231.641 130.164 226.953 117.664 226.953C105.359 226.953 94.8125 231.543 86.0234 240.723C77.2344 249.902 72.8398 261.133 72.8398 274.414V311.621C72.8398 324.316 77.2344 335.449 86.0234 345.02C94.8125 354.395 105.359 359.082 117.664 359.082ZM403.309 430.859C377.918 430.859 355.359 425.391 335.633 414.453C316.102 403.516 300.77 388.965 289.637 370.801C278.699 352.441 273.23 332.324 273.23 310.449L273.816 120.312C273.816 98.0469 279.383 78.0273 290.516 60.2539C301.453 42.2852 316.688 27.9297 336.219 17.1875C355.75 6.25 378.113 0.78125 403.309 0.78125C429.09 0.78125 451.551 6.15234 470.691 16.8945C489.832 27.6367 504.871 42.0898 515.809 60.2539C526.941 78.2227 532.508 98.2422 532.508 120.312L533.094 310.449C533.094 332.129 527.625 352.148 516.688 370.508C505.555 388.867 490.223 403.516 470.691 414.453C451.355 425.391 428.895 430.859 403.309 430.859ZM460.438 310.449L459.852 120.312C459.852 106.25 454.578 94.8242 444.031 86.0352C433.68 77.2461 420.105 72.8516 403.309 72.8516C386.121 72.8516 372.352 77.2461 362 86.0352C351.648 94.8242 346.473 106.25 346.473 120.312V310.449C346.473 325.293 351.648 337.012 362 345.605C372.352 354.199 386.121 358.496 403.309 358.496C420.105 358.496 433.777 354.199 444.324 345.605C455.066 337.012 460.438 325.293 460.438 310.449ZM683.973 430.859C662.879 430.859 643.348 425.391 625.379 414.453C607.41 403.516 593.055 388.867 582.312 370.508C571.766 351.953 566.492 331.738 566.492 309.863V297.852C566.492 296.289 567.469 295.508 569.422 295.508H636.805C638.367 295.508 639.148 296.289 639.148 297.852V309.863C639.148 322.949 643.543 334.277 652.332 343.848C661.121 353.223 671.668 357.91 683.973 357.91C696.473 357.91 707.117 353.223 715.906 343.848C724.695 334.277 729.09 322.949 729.09 309.863V268.262C715.027 274.316 699.988 277.344 683.973 277.344C662.293 277.344 642.762 271.973 625.379 261.23C607.801 250.488 593.641 236.035 582.898 217.871C572.352 199.512 567.078 179.395 567.078 157.52V120.312C567.078 98.0469 572.449 78.0273 583.191 60.2539C593.738 42.2852 607.898 27.9297 625.672 17.1875C643.445 6.25 662.879 0.78125 683.973 0.78125C705.262 0.78125 724.793 6.15234 742.566 16.8945C760.34 27.6367 774.598 42.1875 785.34 60.5469C796.277 78.7109 801.746 98.6328 801.746 120.312V157.52L802.332 309.863C802.332 331.738 796.961 351.855 786.219 370.215C775.477 388.574 761.121 403.32 743.152 414.453C725.379 425.391 705.652 430.859 683.973 430.859ZM683.973 204.688C696.863 204.688 707.605 200.098 716.199 190.918C724.793 181.738 729.09 170.605 729.09 157.52V120.312C729.09 107.422 724.695 96.2891 715.906 86.9141C707.117 77.5391 696.473 72.8516 683.973 72.8516C671.668 72.8516 661.219 77.4414 652.625 86.6211C644.031 95.8008 639.734 107.031 639.734 120.312V157.52C639.734 170.215 644.031 181.25 652.625 190.625C661.219 200 671.668 204.688 683.973 204.688ZM971.082 430.859C945.691 430.859 923.133 425.391 903.406 414.453C883.875 403.516 868.543 388.965 857.41 370.801C846.473 352.441 841.004 332.324 841.004 310.449L841.59 120.312C841.59 98.0469 847.156 78.0273 858.289 60.2539C869.227 42.2852 884.461 27.9297 903.992 17.1875C923.523 6.25 945.887 0.78125 971.082 0.78125C996.863 0.78125 1019.32 6.15234 1038.46 16.8945C1057.61 27.6367 1072.64 42.0898 1083.58 60.2539C1094.71 78.2227 1100.28 98.2422 1100.28 120.312L1100.87 310.449C1100.87 332.129 1095.4 352.148 1084.46 370.508C1073.33 388.867 1058 403.516 1038.46 414.453C1019.13 425.391 996.668 430.859 971.082 430.859ZM1028.21 310.449L1027.62 120.312C1027.62 106.25 1022.35 94.8242 1011.8 86.0352C1001.45 77.2461 987.879 72.8516 971.082 72.8516C953.895 72.8516 940.125 77.2461 929.773 86.0352C919.422 94.8242 914.246 106.25 914.246 120.312V310.449C914.246 325.293 919.422 337.012 929.773 345.605C940.125 354.199 953.895 358.496 971.082 358.496C987.879 358.496 1001.55 354.199 1012.1 345.605C1022.84 337.012 1028.21 325.293 1028.21 310.449Z"
                fill="black"
              />
            </svg>
            <Link to="/" className="text-2xl font-semibold text-primary ml-2 ">
              Prism
            </Link>
            <div className="h-6 w-0.5 bg-gray-200 ml-4 mr-4"></div>

            <img
              src="/customer-logo.png"
              alt="Customer Logo"
              className="w-auto h-5 mr-4"
            />
            {getMenuItems()}
          </div>

          <div className="cursor-pointer flex items-center space-x-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div
                  className="flex items-center space-x-4"
                  data-testid="dropdown-trigger"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage alt={currentUserEmail || ""} />
                    <AvatarFallback>
                      <User className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium hidden sm:inline-block">
                    {currentUserEmail || ""}
                  </span>
                  <span className="sr-only">Open menu</span>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={async () => {
                    trackEvent("menu_click:sign_out", { action: "sign_out" });
                    if (signOut) {
                      await signOut();
                    }
                  }}
                >
                  Sign Out
                  <LogOut className="ml-2 h-4 w-4" />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
